// src/pages/TradePage.jsx — propose, accept, reject trades between teams.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { auth, db } from "../firebase";
import { ref, onValue } from "firebase/database";
import { IPL_TEAMS } from "../players";
import { proposeTrade, respondTrade, subscribeTrades } from "../services/tradeApi";

function PlayerPill({ player, selected, onClick, side }) {
  const teamColor = IPL_TEAMS[player?.team]?.color || "var(--text-dim)";
  const selColor  = side === "offer" ? "var(--gold)" : "var(--blue)";
  return (
    <div onClick={onClick} style={{
      padding: "7px 12px", cursor: "pointer",
      display: "flex", alignItems: "center", gap: 8,
      borderBottom: "1px solid var(--border)",
      background: selected ? (side === "offer" ? "rgba(245,166,35,0.08)" : "rgba(96,165,250,0.08)") : "transparent",
      borderLeft: selected ? `3px solid ${selColor}` : "3px solid transparent",
      transition: "background 0.1s",
    }}>
      <span className={`role-chip role-${player.role}`}>{player.role}</span>
      <span style={{ fontSize: 13, fontWeight: selected ? 700 : 500, flex: 1 }}>{player.name}</span>
      <span className="team-chip" style={{ color: teamColor }}>{player.team}</span>
      {selected && <span style={{ color: selColor, fontSize: 12 }}>✓</span>}
    </div>
  );
}

function TradeCard({ trade, mySlot, playerMap, teamDisplayName, onAccept, onReject, onCancel }) {
  const dn = teamDisplayName || (s => s);
  const isIncoming  = trade.toTeam   === mySlot && trade.status === "pending";
  const isOutgoing  = trade.fromTeam === mySlot && trade.status === "pending";
  const statusColor = {
    pending:   "var(--gold)",
    accepted:  "var(--green)",
    rejected:  "var(--red)",
    cancelled: "var(--text-muted)",
  }[trade.status] || "var(--text-dim)";

  const getPlayer = (id) => playerMap[String(id)] || { name: `#${id}`, role: "?", team: "?" };

  return (
    <div style={{
      background: "var(--bg3)", border: `1px solid ${isIncoming ? "rgba(96,165,250,0.3)" : "var(--border)"}`,
      borderRadius: "var(--radius)", overflow: "hidden", marginBottom: 10,
    }}>
      <div style={{
        padding: "10px 14px", borderBottom: "1px solid var(--border)",
        display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8,
      }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: 13 }}>
            {dn(trade.fromTeam)} → {dn(trade.toTeam)}
          </span>
          {isIncoming && <span className="pill blue" style={{ marginLeft: 8, fontSize: 8 }}>Incoming</span>}
          {isOutgoing && <span className="pill muted" style={{ marginLeft: 8, fontSize: 8 }}>Sent</span>}
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: statusColor, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          {trade.status}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
        {/* Offered (fromTeam gives) */}
        <div style={{ borderRight: "1px solid var(--border)", padding: "10px 14px" }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", color: "var(--gold)",
            textTransform: "uppercase", marginBottom: 8 }}>
            {dn(trade.fromTeam)} sends
          </div>
          {(trade.offeredIds || []).map(id => {
            const p = getPlayer(id);
            return (
              <div key={id} style={{ fontSize: 12, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                <span className={`role-chip role-${p.role}`}>{p.role}</span>
                {p.name}
              </div>
            );
          })}
        </div>
        {/* Requested (toTeam gives) */}
        <div style={{ padding: "10px 14px" }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", color: "var(--blue)",
            textTransform: "uppercase", marginBottom: 8 }}>
            {dn(trade.toTeam)} sends
          </div>
          {(trade.requestedIds || []).map(id => {
            const p = getPlayer(id);
            return (
              <div key={id} style={{ fontSize: 12, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                <span className={`role-chip role-${p.role}`}>{p.role}</span>
                {p.name}
              </div>
            );
          })}
        </div>
      </div>

      {trade.message && (
        <div style={{ padding: "8px 14px", borderTop: "1px solid var(--border)",
          fontSize: 12, color: "var(--text-dim)", fontStyle: "italic" }}>
          "{trade.message}"
        </div>
      )}

      {/* Actions */}
      {(isIncoming || isOutgoing) && (
        <div style={{ padding: "10px 14px", borderTop: "1px solid var(--border)", display: "flex", gap: 8 }}>
          {isIncoming && (
            <>
              <button className="primary" onClick={() => onAccept(trade.id)} style={{ fontSize: 12 }}>
                ✓ Accept Trade
              </button>
              <button className="danger" onClick={() => onReject(trade.id)} style={{ fontSize: 12 }}>
                ✗ Reject
              </button>
            </>
          )}
          {isOutgoing && (
            <button className="mini danger" onClick={() => onCancel(trade.id)} style={{ fontSize: 12 }}>
              Cancel Proposal
            </button>
          )}
        </div>
      )}

      <div style={{ padding: "4px 14px 8px", fontSize: 10, color: "var(--text-muted)" }}>
        Proposed {new Date(trade.proposedAt).toLocaleDateString()}
        {trade.respondedAt && ` · Resolved ${new Date(trade.respondedAt).toLocaleDateString()}`}
      </div>
    </div>
  );
}

export function TradePage({ leagueCode }) {
  const uid = auth.currentUser?.uid;

  const [room,   setRoom]   = useState(null);
  const [trades, setTrades] = useState({});
  const [status, setStatus] = useState("");
  const [view,   setView]   = useState("my"); // "my" | "propose" | "history"

  // Propose form state
  const [targetTeam,    setTargetTeam]    = useState("");
  const [offeredIds,    setOfferedIds]    = useState(new Set());
  const [requestedIds,  setRequestedIds]  = useState(new Set());
  const [message,       setMessage]       = useState("");

  useEffect(() => {
    if (!leagueCode) return;
    const code = leagueCode.toUpperCase();
    const u1 = onValue(ref(db, `rooms/${code}`), s => setRoom(s.val() || null));
    const u2 = subscribeTrades(db, leagueCode, setTrades);
    return () => { u1(); u2(); };
  }, [leagueCode]);

  const mySlot = useMemo(() => {
    if (!uid || !room?.slots) return null;
    const entry = Object.entries(room.slots).find(([, v]) => v?.claimedBy === uid);
    return entry ? entry[0] : null;
  }, [uid, room?.slots]);

  const teamDisplayName = useCallback((slot) =>
    room?.teamCustom?.[slot]?.name || room?.slots?.[slot]?.name || slot,
  [room]);

  const playerMap = useMemo(() => room?.players || {}, [room]);
  const allTeams  = useMemo(() => (room?.teams || []).map(t => t.name), [room]);
  const otherTeams = useMemo(() => allTeams.filter(t => t !== mySlot), [allTeams, mySlot]);

  const myRoster = useMemo(() => {
    if (!mySlot || !room) return [];
    const team = (room.teams || []).find(t => t.name === mySlot);
    return (team?.squadIds || []).map(id => playerMap[String(id)]).filter(Boolean);
  }, [mySlot, room, playerMap]);

  const targetRoster = useMemo(() => {
    if (!targetTeam || !room) return [];
    const team = (room.teams || []).find(t => t.name === targetTeam);
    return (team?.squadIds || []).map(id => playerMap[String(id)]).filter(Boolean);
  }, [targetTeam, room, playerMap]);

  const toggleOffer   = useCallback((id) => {
    setOfferedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);
  const toggleRequest = useCallback((id) => {
    setRequestedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  const submitTrade = async () => {
    if (!mySlot || !targetTeam) return setStatus("Select a target team.");
    if (offeredIds.size === 0)   return setStatus("Select at least one player to offer.");
    if (requestedIds.size === 0) return setStatus("Select at least one player to request.");
    setStatus("Proposing…");
    try {
      await proposeTrade(db, leagueCode, {
        fromTeam: mySlot,
        toTeam: targetTeam,
        offeredIds:   [...offeredIds],
        requestedIds: [...requestedIds],
        message: message.trim() || null,
      });
      setStatus("✓ Trade proposal sent!");
      // Send email notification (best-effort, don't block UI)
      fetch("/api/notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "trade_offer",
          leagueCode,
          fromTeam: mySlot,
          toTeam: targetTeam,
          offeredNames:   [...offeredIds].map(id => playerMap[String(id)]?.name).filter(Boolean),
          requestedNames: [...requestedIds].map(id => playerMap[String(id)]?.name).filter(Boolean),
          message: message.trim() || null,
        }),
      }).catch(() => {});
      setOfferedIds(new Set()); setRequestedIds(new Set()); setMessage(""); setView("my");
    } catch (e) { setStatus("Failed: " + e.message); }
  };

  const handleAccept = async (id) => {
    setStatus("Accepting trade…");
    try { await respondTrade(db, leagueCode, id, "accepted"); setStatus("✓ Trade accepted!"); }
    catch (e) { setStatus("Failed: " + e.message); }
  };
  const handleReject = async (id) => {
    try { await respondTrade(db, leagueCode, id, "rejected"); setStatus("Trade rejected."); }
    catch (e) { setStatus("Failed: " + e.message); }
  };
  const handleCancel = async (id) => {
    try { await respondTrade(db, leagueCode, id, "cancelled"); setStatus("Trade cancelled."); }
    catch (e) { setStatus("Failed: " + e.message); }
  };

  const tradeList     = Object.values(trades);
  const activeTrades  = tradeList.filter(t => t.status === "pending" && (t.fromTeam === mySlot || t.toTeam === mySlot));
  const incomingCount = tradeList.filter(t => t.status === "pending" && t.toTeam === mySlot).length;
  const historyTrades = tradeList.filter(t => t.status !== "pending" && (t.fromTeam === mySlot || t.toTeam === mySlot))
    .sort((a, b) => (b.respondedAt || b.proposedAt) - (a.respondedAt || a.proposedAt));

  return (
    <div>
      {/* Header */}
      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-head">
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, fontFamily: "'Playfair Display', Georgia, serif" }}>
              Trades
            </div>
            <div style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 2 }}>
              {mySlot ? teamDisplayName(mySlot) : "No team slot"}
              {incomingCount > 0 && <span className="pill red" style={{ marginLeft: 8 }}>{incomingCount} incoming</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {[["my","My Trades"],["propose","Propose"],["history","History"]].map(([v,l]) => (
              <button key={v} className={view === v ? "primary" : ""} onClick={() => setView(v)} type="button">
                {l}
                {v === "my" && incomingCount > 0 && <span className="pill red" style={{ marginLeft: 4, fontSize: 8 }}>{incomingCount}</span>}
              </button>
            ))}
          </div>
        </div>
        {status && (
          <div style={{ padding: "8px 16px", borderTop: "1px solid var(--border)", fontSize: 13,
            color: status.startsWith("✓") ? "var(--green)" : status.startsWith("Failed") ? "var(--red)" : "var(--gold)" }}>
            {status}
          </div>
        )}
      </div>

      {/* ── My Trades ── */}
      {view === "my" && (
        activeTrades.length === 0 ? (
          <div className="panel" style={{ padding: 24, textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>🤝</div>
            <div style={{ color: "var(--text-dim)" }}>No active trades. Propose one to get started.</div>
            <button className="primary" onClick={() => setView("propose")} style={{ marginTop: 14 }}>
              Propose a Trade
            </button>
          </div>
        ) : (
          activeTrades.map(t => (
            <TradeCard key={t.id} trade={t} mySlot={mySlot} playerMap={playerMap}
              teamDisplayName={teamDisplayName}
              onAccept={handleAccept} onReject={handleReject} onCancel={handleCancel} />
          ))
        )
      )}

      {/* ── Propose ── */}
      {view === "propose" && (
        <div>
          {/* Step 1: Pick target */}
          <div className="panel" style={{ marginBottom: 12, padding: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Step 1 — Choose target team</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {otherTeams.map(t => (
                <button key={t}
                  className={targetTeam === t ? "primary" : ""}
                  onClick={() => { setTargetTeam(t); setRequestedIds(new Set()); }}
                  style={{ fontSize: 12 }}>
                  {teamDisplayName(t)}
                </button>
              ))}
            </div>
          </div>

          {targetTeam && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              {/* My roster — offer */}
              <div className="panel">
                <div className="panel-head">
                  <div style={{ fontWeight: 700, fontSize: 13 }}>Your Players — Offer</div>
                  <span className="pill" style={{ fontSize: 8 }}>{offeredIds.size} selected</span>
                </div>
                {myRoster.map(p => (
                  <PlayerPill key={p.id} player={p} selected={offeredIds.has(p.id)}
                    onClick={() => toggleOffer(p.id)} side="offer" />
                ))}
              </div>
              {/* Their roster — request */}
              <div className="panel">
                <div className="panel-head">
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{teamDisplayName(targetTeam)} — Request</div>
                  <span className="pill blue" style={{ fontSize: 8 }}>{requestedIds.size} selected</span>
                </div>
                {targetRoster.map(p => (
                  <PlayerPill key={p.id} player={p} selected={requestedIds.has(p.id)}
                    onClick={() => toggleRequest(p.id)} side="request" />
                ))}
              </div>
            </div>
          )}

          {targetTeam && (
            <div className="panel" style={{ padding: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>
                Step 3 — Add a message (optional)
              </div>
              <input className="search-box" value={message} onChange={e => setMessage(e.target.value)}
                placeholder="Talk some trash…" style={{ marginBottom: 12 }} />
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <button className="primary" onClick={submitTrade}
                  disabled={offeredIds.size === 0 || requestedIds.size === 0}>
                  Send Trade Proposal
                </button>
                <span style={{ fontSize: 12, color: "var(--text-dim)" }}>
                  Offering {offeredIds.size} · Requesting {requestedIds.size}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── History ── */}
      {view === "history" && (
        historyTrades.length === 0 ? (
          <div className="panel" style={{ padding: 20, textAlign: "center", color: "var(--text-dim)" }}>
            No trade history yet.
          </div>
        ) : (
          historyTrades.map(t => (
            <TradeCard key={t.id} trade={t} mySlot={mySlot} playerMap={playerMap}
              teamDisplayName={teamDisplayName}
              onAccept={handleAccept} onReject={handleReject} onCancel={handleCancel} />
          ))
        )
      )}
    </div>
  );
}
